import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, TrendingUp, AlertTriangle, Check, Plus, Edit3,
  ChevronDown, ChevronUp, Calendar, DollarSign, RefreshCw, X,
  Send, Gift, Loader2, ExternalLink, Copy, Zap, CheckCircle
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/billing';

const STATUS_CONFIG = {
  paid: { label: 'Pagato', color: '#059669', bg: 'rgba(5,150,105,0.1)', border: 'rgba(5,150,105,0.3)' },
  pending: { label: 'In attesa', color: '#d97706', bg: 'rgba(217,119,6,0.1)', border: 'rgba(217,119,6,0.3)' },
  overdue: { label: 'Scaduto', color: '#dc2626', bg: 'rgba(220,38,38,0.1)', border: 'rgba(220,38,38,0.3)' },
  cancelled: { label: 'Cancellato', color: '#6b7280', bg: 'rgba(107,114,128,0.1)', border: 'rgba(107,114,128,0.3)' },
  lifetime: { label: 'Lifetime', color: '#c9a84c', bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.3)' },
  no_billing: { label: 'Non configurato', color: '#555', bg: 'rgba(100,100,100,0.08)', border: 'rgba(100,100,100,0.15)' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.no_billing;
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
}

export function BillingTab({ storedKey }) {
  const [billing, setBilling] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [editSlug, setEditSlug] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({});
  const [showPayment, setShowPayment] = useState(null);
  const [showLifetime, setShowLifetime] = useState(false);
  const [lifetimeForm, setLifetimeForm] = useState({ email: '', client_name: '', price_eur: '', payment_link: '', notes: '' });
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offers, setOffers] = useState([]);

  // Stripe states
  const [stripeLoading, setStripeLoading] = useState(null); // slug o 'lifetime'
  const [stripeModal, setStripeModal] = useState(null); // { url, session_id, tenant, amount, plan, email_sent }
  const [copied, setCopied] = useState(false);
  const [generatingLifetimeLink, setGeneratingLifetimeLink] = useState(false);

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, oRes, offRes] = await Promise.all([
        axios.get(API, { headers }),
        axios.get(`${API}/summary/overview`, { headers }),
        axios.get(`${API}/lifetime-offers`, { headers }),
      ]);
      setBilling(bRes.data.billing);
      setOverview(oRes.data);
      setOffers(offRes.data.offers || []);
    } catch { toast.error('Errore caricamento billing.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveBilling = async (slug) => {
    try {
      await axios.put(`${API}/${slug}`, editForm, { headers });
      toast.success('Billing aggiornato.');
      setEditSlug(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
  };

  const addPayment = async (slug) => {
    try {
      await axios.post(`${API}/${slug}/payment`, paymentForm, { headers });
      toast.success('Pagamento registrato!');
      setShowPayment(null);
      setPaymentForm({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
  };

  const sendLifetimeOffer = async () => {
    if (!lifetimeForm.email || !lifetimeForm.client_name || !lifetimeForm.price_eur) {
      toast.error('Compila email, nome cliente e prezzo.');
      return;
    }
    setSendingOffer(true);
    try {
      const res = await axios.post(`${API}/lifetime-offer`, {
        ...lifetimeForm,
        price_eur: parseFloat(lifetimeForm.price_eur),
      }, { headers });
      toast.success(res.data.email_sent ? 'Offerta inviata via email!' : 'Offerta salvata (SMTP non configurato).');
      setShowLifetime(false);
      setLifetimeForm({ email: '', client_name: '', price_eur: '', payment_link: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio offerta.'); }
    finally { setSendingOffer(false); }
  };

  // ── Stripe ──────────────────────────────────────────────────────────────────

  const generateStripeLink = async (slug) => {
    setStripeLoading(slug);
    try {
      const res = await axios.post(`${API}/stripe/checkout/${slug}`, {
        origin_url: window.location.origin,
        send_email: true,
      }, { headers });
      setStripeModal({
        url: res.data.checkout_url,
        session_id: res.data.session_id,
        tenant: res.data.tenant,
        amount: res.data.amount_eur,
        plan: res.data.plan,
        email_sent: res.data.email_sent,
        type: 'subscription',
      });
      toast.success('Link Stripe generato!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione link Stripe.');
    } finally {
      setStripeLoading(null);
    }
  };

  const generateStripeLifetimeLink = async () => {
    if (!lifetimeForm.email || !lifetimeForm.client_name || !lifetimeForm.price_eur) {
      toast.error('Compila email, nome cliente e prezzo per generare il link.');
      return;
    }
    setGeneratingLifetimeLink(true);
    try {
      const res = await axios.post(`${API}/stripe/lifetime-checkout`, {
        origin_url: window.location.origin,
        email: lifetimeForm.email,
        client_name: lifetimeForm.client_name,
        price_eur: parseFloat(lifetimeForm.price_eur),
        notes: lifetimeForm.notes,
        tenant_slug: '',
      }, { headers });
      // Auto-compila il campo payment_link
      setLifetimeForm(p => ({ ...p, payment_link: res.data.checkout_url }));
      setStripeModal({
        url: res.data.checkout_url,
        session_id: res.data.session_id,
        tenant: res.data.client_name,
        amount: res.data.amount_eur,
        plan: 'lifetime',
        email_sent: false,
        type: 'lifetime',
      });
      toast.success('Link Stripe Lifetime generato!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione link Lifetime.');
    } finally {
      setGeneratingLifetimeLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Link copiato!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div data-testid="billing-tab">
      {/* Overview KPIs */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'ARR', value: `€${overview.arr?.toLocaleString('it-IT')}`, icon: TrendingUp, color: '#c9a84c' },
            { label: 'MRR', value: `€${Math.round(overview.mrr)?.toLocaleString('it-IT')}`, icon: DollarSign, color: '#059669' },
            { label: 'Totale incassato', value: `€${overview.total_collected?.toLocaleString('it-IT')}`, icon: Check, color: '#7c3aed' },
            { label: 'Scaduti', value: overview.overdue_count, icon: AlertTriangle, color: '#dc2626' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color }} />
                <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{label}</p>
              </div>
              <p className="font-serif text-2xl font-light" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-xl font-light" style={{ color: 'var(--lux-text)' }}>Abbonamenti Tenant</h3>
        <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm"
          style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={12} /> Aggiorna
        </button>
      </div>

      {/* Billing list */}
      <div className="space-y-3">
        {billing.map((b) => (
          <div key={b.slug} className="rounded-sm overflow-hidden" style={{ border: '1px solid var(--lux-border-1)' }}>
            {/* Row header */}
            <div className="flex items-center gap-4 px-5 py-3.5 cursor-pointer"
              style={{ background: 'var(--lux-surface-1)' }}
              onClick={() => setExpanded(expanded === b.slug ? null : b.slug)}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{b.name}</p>
                  <code className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted-2)' }}>{b.slug}</code>
                  <StatusBadge status={b.status} />
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs" style={{ color: 'var(--lux-text-muted)' }}>
                  <span>Piano: {b.plan || '—'}</span>
                  {b.billing?.amount_eur && <span>€{b.billing.amount_eur}/anno</span>}
                  {b.billing?.renewal_date && <span>Rinnovo: {b.billing.renewal_date?.slice(0, 10)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={e => { e.stopPropagation(); setEditSlug(b.slug); setEditForm(b.billing || { slug: b.slug, plan: b.plan }); }}
                  className="p-1.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)' }}>
                  <Edit3 size={12} />
                </button>
                <button onClick={e => { e.stopPropagation(); setShowPayment(b.slug); setPaymentForm({ amount_eur: b.billing?.amount_eur || 0 }); }}
                  className="px-3 py-1.5 text-[10px] font-medium rounded-sm"
                  style={{ background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.3)' }}>
                  <Plus size={10} className="inline mr-1" /> Pagamento
                </button>
                {b.plan !== 'lifetime' && (
                  <button
                    onClick={e => { e.stopPropagation(); generateStripeLink(b.slug); }}
                    disabled={stripeLoading === b.slug}
                    data-testid={`stripe-link-btn-${b.slug}`}
                    className="px-3 py-1.5 text-[10px] font-medium rounded-sm flex items-center gap-1"
                    style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
                    {stripeLoading === b.slug
                      ? <Loader2 size={10} className="animate-spin" />
                      : <Zap size={10} />}
                    Link Stripe
                  </button>
                )}
                {expanded === b.slug ? <ChevronUp size={14} style={{ color: 'var(--lux-text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--lux-text-muted)' }} />}
              </div>
            </div>

            {/* Expanded: payment history */}
            {expanded === b.slug && (
              <div className="px-5 pb-4 pt-2" style={{ background: 'var(--lux-surface-1)', borderTop: '1px solid var(--lux-border-1)' }}>
                <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--lux-gold)' }}>Storico Pagamenti</p>
                {b.payments?.length > 0 ? (
                  <div className="space-y-2">
                    {b.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-sm text-xs"
                        style={{ background: 'var(--lux-surface-3)' }}>
                        <span style={{ color: 'var(--lux-text)' }}>€{p.amount_eur}</span>
                        <span style={{ color: 'var(--lux-text-muted)' }}>{p.method}</span>
                        <span style={{ color: 'var(--lux-text-muted)' }}>{p.paid_at?.slice(0, 10)}</span>
                        {p.invoice_number && <span style={{ color: 'var(--lux-text-muted-2)' }}>#{p.invoice_number}</span>}
                        {p.notes && <span style={{ color: 'var(--lux-text-muted-3)' }}>{p.notes}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>Nessun pagamento registrato.</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit billing modal */}
      {editSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-sm p-6 space-y-4" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>Modifica Billing — {editSlug}</h4>
              <button onClick={() => setEditSlug(null)}><X size={18} style={{ color: 'var(--lux-text-muted)' }} /></button>
            </div>
            {[
              { key: 'plan', label: 'Piano', type: 'select', opts: ['essenziale', 'professional', 'elite', 'lifetime'] },
              { key: 'amount_eur', label: 'Importo €/anno', type: 'number' },
              { key: 'billing_cycle', label: 'Ciclo', type: 'select', opts: ['annual', 'monthly', 'lifetime'] },
              { key: 'status', label: 'Stato', type: 'select', opts: ['paid', 'pending', 'overdue', 'cancelled'] },
              { key: 'started_at', label: 'Data inizio', type: 'date' },
              { key: 'expires_at', label: 'Data scadenza', type: 'date' },
              { key: 'invoice_number', label: 'N. Fattura', type: 'text' },
              { key: 'payment_notes', label: 'Note', type: 'text' },
            ].map(({ key, label, type, opts }) => (
              <div key={key}>
                <label className="block text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--lux-text-muted)' }}>{label}</label>
                {type === 'select'
                  ? <select value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-sm"
                      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                      {opts.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  : <input type={type} value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-sm"
                      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => saveBilling(editSlug)} className="flex-1 py-2.5 text-sm font-semibold" style={{ background: 'var(--lux-gold)', color: '#000' }}>Salva</button>
              <button onClick={() => setEditSlug(null)} className="px-4 py-2.5 text-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>Annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* Add payment modal */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-sm rounded-sm p-6 space-y-4" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>Registra Pagamento</h4>
              <button onClick={() => setShowPayment(null)}><X size={18} style={{ color: 'var(--lux-text-muted)' }} /></button>
            </div>
            {[
              { key: 'amount_eur', label: 'Importo €', type: 'number' },
              { key: 'paid_at', label: 'Data pagamento', type: 'date' },
              { key: 'method', label: 'Metodo', type: 'select', opts: ['bank_transfer', 'paypal', 'stripe', 'cash', 'other'] },
              { key: 'invoice_number', label: 'N. Fattura', type: 'text' },
              { key: 'notes', label: 'Note', type: 'text' },
            ].map(({ key, label, type, opts }) => (
              <div key={key}>
                <label className="block text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--lux-text-muted)' }}>{label}</label>
                {type === 'select'
                  ? <select value={paymentForm[key] || ''} onChange={e => setPaymentForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-sm"
                      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                      {opts.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  : <input type={type} value={paymentForm[key] || ''} onChange={e => setPaymentForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-sm"
                      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => addPayment(showPayment)} className="flex-1 py-2.5 text-sm font-semibold" style={{ background: '#059669', color: '#fff' }}>Registra</button>
              <button onClick={() => setShowPayment(null)} className="px-4 py-2.5 text-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
      {/* Lifetime Offer Section */}
      <div className="rounded-sm" style={{ border: '1px solid var(--lux-gold)33', background: 'rgba(201,168,76,0.04)' }}>
        <button onClick={() => setShowLifetime(p => !p)}
          className="w-full flex items-center justify-between px-6 py-4"
          data-testid="toggle-lifetime-section">
          <div className="flex items-center gap-3">
            <Gift size={16} style={{ color: 'var(--lux-gold)' }} />
            <div className="text-left">
              <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>Offerta Lifetime Personalizzata</p>
              <p className="text-[11px]" style={{ color: 'var(--lux-text-muted)' }}>Invia proposta con prezzo custom via email · {offers.length} offerte inviate</p>
            </div>
          </div>
          <ChevronDown size={14} style={{ color: 'var(--lux-gold)', transform: showLifetime ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>

        {showLifetime && (
          <div className="px-6 pb-6 border-t" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              {[
                { key: 'client_name', label: 'Nome Cliente / Struttura', placeholder: 'es. Hotel Palazzo Venezia', type: 'text' },
                { key: 'email', label: 'Email Destinatario', placeholder: 'cliente@email.com', type: 'email' },
                { key: 'price_eur', label: 'Prezzo Lifetime (€)', placeholder: 'es. 12000', type: 'number' },
                { key: 'payment_link', label: 'Link Pagamento (opzionale)', placeholder: 'https://...', type: 'url' },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--lux-text-muted)' }}>{label}</label>
                  <input type={type} placeholder={placeholder}
                    value={lifetimeForm[key]}
                    onChange={e => setLifetimeForm(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm rounded-sm"
                    style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                    data-testid={`lifetime-${key}`}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--lux-text-muted)' }}>Note (opzionale)</label>
                <textarea placeholder="Aggiungi note o condizioni speciali..."
                  value={lifetimeForm.notes}
                  onChange={e => setLifetimeForm(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2.5 text-sm rounded-sm resize-none"
                  style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)', outline: 'none' }}
                  data-testid="lifetime-notes"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={sendLifetimeOffer} disabled={sendingOffer}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-sm transition-opacity"
                style={{ background: 'var(--lux-gold)', color: '#000', opacity: sendingOffer ? 0.7 : 1 }}
                data-testid="send-lifetime-offer-btn">
                {sendingOffer ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sendingOffer ? 'Invio...' : 'Invia Offerta Email'}
              </button>
              <button
                onClick={generateStripeLifetimeLink}
                disabled={generatingLifetimeLink}
                data-testid="generate-stripe-lifetime-btn"
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-sm"
                style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.4)', opacity: generatingLifetimeLink ? 0.7 : 1 }}>
                {generatingLifetimeLink ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                {generatingLifetimeLink ? 'Generazione...' : 'Genera Link Stripe'}
              </button>
              <button onClick={() => setShowLifetime(false)}
                className="px-4 py-2.5 text-sm"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                Annulla
              </button>
            </div>

            {/* Storico offerte */}
            {offers.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: 'var(--lux-text-muted)' }}>Offerte Inviate</p>
                <div className="space-y-2">
                  {offers.slice(0, 5).map((o, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 rounded-sm"
                      style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
                      <div>
                        <p className="text-xs font-medium" style={{ color: 'var(--lux-text)' }}>{o.client_name}</p>
                        <p className="text-[11px]" style={{ color: 'var(--lux-text-muted)' }}>{o.email} · {new Date(o.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-light" style={{ color: 'var(--lux-gold)' }}>€{o.price_eur?.toLocaleString('it-IT')}</p>
                        {o.payment_link && (
                          <a href={o.payment_link} target="_blank" rel="noreferrer"
                            className="text-[10px] underline" style={{ color: 'var(--lux-text-muted)' }}>
                            link pagamento
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stripe Link Modal */}
      {stripeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="w-full max-w-lg rounded-sm p-7 space-y-5" style={{ background: 'var(--lux-surface-1)', border: '1px solid rgba(99,102,241,0.4)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                  <CheckCircle size={16} style={{ color: '#818cf8' }} />
                </div>
                <div>
                  <h4 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>
                    Link Stripe Generato
                  </h4>
                  <p className="text-[11px]" style={{ color: 'var(--lux-text-muted)' }}>
                    {stripeModal.type === 'lifetime' ? 'Offerta Lifetime' : `Piano ${stripeModal.plan?.toUpperCase()}`} · {stripeModal.tenant} · €{stripeModal.amount?.toLocaleString('it-IT')}
                  </p>
                </div>
              </div>
              <button onClick={() => setStripeModal(null)}><X size={18} style={{ color: 'var(--lux-text-muted)' }} /></button>
            </div>

            {/* Email status */}
            {stripeModal.email_sent && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs"
                style={{ background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.3)', color: '#059669' }}>
                <Check size={12} /> Email inviata automaticamente al cliente
              </div>
            )}

            {/* URL display */}
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted)' }}>Link di pagamento Stripe</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={stripeModal.url}
                  className="flex-1 px-3 py-2.5 text-xs rounded-sm font-mono"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', outline: 'none' }}
                  data-testid="stripe-checkout-url"
                />
                <button
                  onClick={() => copyToClipboard(stripeModal.url)}
                  data-testid="copy-stripe-url-btn"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold rounded-sm transition-all"
                  style={{
                    background: copied ? 'rgba(5,150,105,0.15)' : 'rgba(99,102,241,0.15)',
                    color: copied ? '#059669' : '#818cf8',
                    border: `1px solid ${copied ? 'rgba(5,150,105,0.4)' : 'rgba(99,102,241,0.4)'}`,
                  }}>
                  {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                  {copied ? 'Copiato!' : 'Copia'}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <a
                href={stripeModal.url}
                target="_blank"
                rel="noreferrer"
                data-testid="open-stripe-url-btn"
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-sm"
                style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.4)' }}>
                <ExternalLink size={13} /> Apri in Stripe
              </a>
              <button onClick={() => setStripeModal(null)}
                className="px-4 py-2.5 text-sm"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                Chiudi
              </button>
            </div>

            {/* Session ID */}
            <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
              Session ID: <code>{stripeModal.session_id}</code> · Il webhook attiverà il tenant automaticamente dopo il pagamento.
            </p>
          </div>
        </div>
      )}

    </div>
  );
}
